import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { CONSENT_COOKIE, CONSENT_DENIED_MAX_AGE, CONSENT_GRANTED_MAX_AGE } from "@/lib/consent";
import { loadPixel } from "@/lib/meta-pixel";

type Choice = "granted" | "denied";

function writeConsent(value: Choice) {
  const maxAge = value === "granted" ? CONSENT_GRANTED_MAX_AGE : CONSENT_DENIED_MAX_AGE;
  document.cookie = `${CONSENT_COOKIE}=${value}; Max-Age=${maxAge}; Path=/; SameSite=Lax; Secure`;
}

function readConsent(): Choice | "unset" {
  const match = document.cookie.match(new RegExp(`(?:^|; )${CONSENT_COOKIE}=(granted|denied)`));
  return match?.[1] === "granted" || match?.[1] === "denied" ? match[1] : "unset";
}

/**
 * Shown only where the law requires a choice before a measurement cookie is
 * set, and only until one is made. Two buttons of equal weight, nothing
 * pre-selected, no wall across the page: the regulators' description of a
 * valid choice is also the description of a decent one.
 */
export function ConsentBanner() {
  const [decided, setDecided] = useState(false);

  // On a phone the banner spans the screen, and the chat button in the
  // bottom-right corner sat on top of Accept. While the choice is open the
  // page is marked, and the stylesheet keeps the chat button out of the way.
  useEffect(() => {
    if (decided) return;
    document.documentElement.setAttribute("data-consent-open", "");
    return () => document.documentElement.removeAttribute("data-consent-open");
  }, [decided]);

  if (decided) return null;

  const decide = (value: Choice) => {
    writeConsent(value);
    setDecided(true);
    if (value === "granted") loadPixel();
  };

  return (
    <aside
      role="region"
      aria-label="Cookie choice"
      className="tone-ink fixed bottom-4 left-4 right-4 z-40 border border-border p-5 shadow-2xl sm:right-auto sm:max-w-[41rem] sm:px-6"
    >
      {/* A bar, not a card: a card in the corner covered the page's main
          button on a laptop screen until the choice was made. */}
      <div className="sm:flex sm:items-center sm:gap-6">
        <div className="min-w-0">
          <p className="text-sm font-medium">Measurement on this site</p>
          <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
            Adkiro uses the Meta Pixel to measure its own advertising. It stays off until you
            choose, and the choice can be changed at any time on the{" "}
            <a href="/privacy#cookies" className="text-primary underline-offset-4 hover:underline">
              privacy page
            </a>
            .
          </p>
        </div>
        <div className="mt-4 grid shrink-0 grid-cols-2 gap-2 sm:mt-0 sm:w-52">
          <Button variant="outline" size="sm" className="h-10" onClick={() => decide("denied")}>
            Decline
          </Button>
          <Button variant="brand" size="sm" className="h-10" onClick={() => decide("granted")}>
            Accept
          </Button>
        </div>
      </div>
    </aside>
  );
}

/**
 * The same choice, offered to everyone from the privacy page — including
 * visitors in regions where the pixel loads by default, who otherwise would
 * have no way to turn it off.
 */
export function ConsentControls() {
  const [state, setState] = useState<Choice | "unset">("unset");
  useEffect(() => setState(readConsent()), []);

  const apply = (value: Choice) => {
    writeConsent(value);
    setState(value);
    if (value === "granted") loadPixel();
    // A script already in the page cannot be taken back out; a reload
    // renders the page without it.
    else window.location.reload();
  };

  const label =
    state === "granted"
      ? "Measurement is on for this browser."
      : state === "denied"
        ? "Measurement is off for this browser."
        : "No choice has been recorded in this browser yet.";

  return (
    <div className="mt-6 rounded-xl border border-border p-4">
      <p className="text-sm text-muted-foreground">{label}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => apply("denied")}
          disabled={state === "denied"}
        >
          Turn measurement off
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => apply("granted")}
          disabled={state === "granted"}
        >
          Turn measurement on
        </Button>
      </div>
    </div>
  );
}
